import { postRequestOptions } from 'components/ContentReporting/form-utils';
import * as Sentry from 'lib/client/sentry/sentry';
import {
  GetCodeRequest,
  GetCodeResponse,
  ValidateCodeRequest,
  ExternalContentPolicyReportRequest,
  EmployeeContentPolicyReportRequest,
} from 'proto/generated/tintin_content_policy';
import {
  ResolveAssetTypeRequest,
  ResolveAssetTypeResponse,
  ResolveEntityTypeResponse,
} from 'proto/generated/inspector_gadget';

const UNAUTHORIZED = 401;
const FORBIDDEN = 403;
const CREATED = 201;
const TOO_MANY_REQUESTS = 429;

export interface TintinClient {
  getVerificationCode(request: GetCodeRequest): Promise<GetCodeResponse>;
  validateVerificationCode(request: ValidateCodeRequest): Promise<boolean>;
  submitExternalContentPolicyReport(request: ExternalContentPolicyReportRequest): Promise<boolean>;
  submitEmployeeContentPolicyReport(request: EmployeeContentPolicyReportRequest): Promise<boolean>;
  resolveEntityType(request: ResolveEntityTypeRequest): Promise<ResolveEntityTypeResponse>;
  checkAssetExists(request: ResolveAssetTypeRequest): Promise<ResolveAssetTypeResponse>;
}

export interface ResolveEntityTypeRequest {
  uris: Array<string>;
}

export const TintinClientCreator = {
  create(contentReportingUrl: string): TintinClient {
    return {
      async getVerificationCode(request: GetCodeRequest): Promise<GetCodeResponse> {
        const endpoint: string = `${contentReportingUrl}/v2/content-policy-report-form/get-code`;
        // This function is generated now and returns unknown, we see no value into using explicit type unknown here
        const payload = GetCodeRequest.toJSON(request);

        const response: Response = await fetch(endpoint, postRequestOptions(payload));
        const responseJson: any = await response.json();

        if (!response.ok) {
          if (response.status === TOO_MANY_REQUESTS) {
            throw new Error(`You cannot submit a report at this time due to status: TOO_MANY_REQUESTS`);
          }
          const error: Error = new Error(
            `Requested verification code, got status: ${response.status}. Error: ${responseJson.error}`,
          );
          if (response.status !== FORBIDDEN) {
            // HTTP 403 FORBIDDEN is returned when the provided email is not valid
            Sentry.captureException(error);
          }

          throw error;
        }

        return GetCodeResponse.fromJSON(responseJson);
      },

      async validateVerificationCode(request: ValidateCodeRequest): Promise<boolean> {
        const endpoint: string = `${contentReportingUrl}/v2/content-policy-report-form/validate-code`;
        // This function is generated now and returns unknown, we see no value into using explicit type unknown here
        const payload = ValidateCodeRequest.toJSON(request);

        const response: Response = await fetch(endpoint, postRequestOptions(payload));

        if (response.ok) {
          return true;
        }

        if (response.status === UNAUTHORIZED) {
          return false;
        }

        const error: Error = new Error(`Validated verification code, got unexpected status: ${response.status}`);
        Sentry.captureException(error);
        throw error;
      },

      async submitExternalContentPolicyReport(request: ExternalContentPolicyReportRequest): Promise<boolean> {
        const endpoint: string = `${contentReportingUrl}/v2/content-policy-report-form/submit-external-report`;
        // This function is generated now and returns unknown, we see no value into using explicit type unknown here
        const payload = ExternalContentPolicyReportRequest.toJSON(request);

        const response: Response = await fetch(endpoint, postRequestOptions(payload));

        if (response.ok && response.status === CREATED) {
          return true;
        }

        const error: Error = new Error(`Could not submit policy report, got status: ${response.status}`);
        if (response.status >= 500) {
          Sentry.captureException(error);
        }
        throw error;
      },

      async submitEmployeeContentPolicyReport(request: EmployeeContentPolicyReportRequest): Promise<boolean> {
        const endpoint: string = `${contentReportingUrl}/v2/content-policy-report-form/submit-employee-report`;
        // This function is generated now and returns unknown, we see no value into using explicit type unknown here
        const payload = EmployeeContentPolicyReportRequest.toJSON(request);

        const response: Response = await fetch(endpoint, postRequestOptions(payload));

        if (response.ok && response.status === CREATED) {
          return true;
        }

        const error: Error = new Error(`Could not submit employee policy report, got status: ${response.status}`);
        if (response.status >= 500) {
          Sentry.captureException(error);
        }
        throw error;
      },

      async resolveEntityType(request: ResolveEntityTypeRequest): Promise<ResolveEntityTypeResponse> {
        const endpoint: string = `${contentReportingUrl}/v1/entity-type`;
        const response: Response = await fetch(endpoint, postRequestOptions(request));
        const responseJson: ResolveEntityTypeResponse = await response.json();

        if (!response.ok) {
          const error: Error = new Error(`Failed to resolve entity. Got status: ${response.status}.`);
          Sentry.captureException(error);
          throw error;
        }
        return responseJson;
      },

      async checkAssetExists(request: ResolveAssetTypeRequest): Promise<ResolveAssetTypeResponse> {
        const endpoint: string = `${contentReportingUrl}/v1/asset-type`;
        const response: Response = await fetch(endpoint, postRequestOptions(request));
        const responseJson: ResolveAssetTypeResponse = await response.json();

        if (!response.ok) {
          const error: Error = new Error(`Failed to check asset existence. Got status: ${response.status}.`);
          Sentry.captureException(error);
          throw error;
        }
        return responseJson;
      },
    };
  },
};
